// Iter 647 — Scan Cepat Shella: SCAN → PERIKSA → KONFIRMASI (Finalisasi) → SCAN BERIKUTNYA.
//   Memakai mekanisme Scan Cepat Adit (wedge, kamera, panel). Finalisasi tetap via endpoint resmi Shella.
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Camera, CheckCircle2, AlertTriangle, RefreshCw, ScanLine, XCircle } from "lucide-react";
import ScanMockupModal, { playScanBeep } from "./scan_mockup";
import { useWedge, errText, fmt } from "./acc_fast_scan";
import { playTone, TONE_LEGEND } from "./scan_tones";
import { channelMismatch, openChannelFor, takePendingScan, ChannelMismatchNotice } from "./channel";

const BASE = process.env.REACT_APP_BACKEND_URL;
const API = `${BASE}/api`;
const opt = { withCredentials: true, timeout: 20000 };
const MISMATCH = [["ukuran", "Ukuran"], ["warna", "Warna"], ["jumlah", "Jumlah"], ["produk", "Produk/Desain"], ["lainnya", "Lainnya"]];

export function ShellaFastScan({ onOfflineChanged }) {
  const inputRef = useRef(null);
  const [code, setCode] = useState("");
  const [data, setData] = useState(null);
  const [phase, setPhase] = useState("idle");
  const [mm, setMm] = useState(null);
  const [err, setErr] = useState("");
  const [scanRec, setScanRec] = useState("");
  const [res, setRes] = useState(null); // {state: busy|ok|fail, msg, retry}
  const [mis, setMis] = useState(null);
  const [cam, setCam] = useState(false);
  const [recent, setRecent] = useState([]); // [{code, done}]
  const fromCam = useRef(false);
  const last = useRef({ code: "", at: 0 });
  const lock = useRef(false);
  const reqId = useRef(0);
  const readyAt = useRef(0);
  const [note, setNote] = useState("");
  const focus = () => setTimeout(() => inputRef.current?.focus(), 30);

  const lookup = useCallback(async (raw, opts = {}) => {
    const c = String(raw || "").replace(/\s+/g, "").toUpperCase();
    if (!c) return;
    const now = Date.now();
    if (!opts.silent && last.current.code === c && now - last.current.at < 1500) return;
    last.current = { code: c, at: now };
    const id = ++reqId.current;
    if (!opts.silent) {
      setPhase("loading"); setErr(""); setRes(null); setMis(null); setScanRec(""); setCode(""); setNote(""); readyAt.current = 0;
      setRecent(r => [{ code: c, done: false }, ...r.filter(x => x.code !== c)].slice(0, 8));
      axios.post(`${API}/orders/${encodeURIComponent(c)}/barcode-scan`, { device: "shella-fast" }, opt)
        .then(() => id === reqId.current && setScanRec("ok")).catch(() => id === reqId.current && setScanRec("fail"));
    }
    try {
      const r = await axios.get(`${API}/shella/scan/${encodeURIComponent(c)}`, opt);
      if (id !== reqId.current) return r.data;
      setData(r.data); setPhase("ready");
      if (!opts.silent) readyAt.current = Date.now();
      if (r.data.finalized) setRecent(x => x.map(i => i.code === c ? { ...i, done: true } : i));
      if (!opts.silent) playTone(r.data.finalized ? "done" : r.data.eligible ? "ready" : "blocked");
      focus();
      return r.data;
    } catch (e) {
      if (id !== reqId.current) return null;
      const mm = channelMismatch(e);
      if (!opts.silent) { setMm(mm ? { ...mm, code: c } : null); setData(null); setPhase(mm ? "channel" : "error"); setErr(mm ? "" : errText(e)); last.current = { code: "", at: 0 }; playScanBeep({ kind: "err", durationMs: 180 }); }
      focus();
      return null;
    }
  }, []);
  useWedge(lookup);
  useEffect(() => { focus(); const p = takePendingScan("shella"); if (p) lookup(p); }, [lookup]);

  const confirm = async () => {
    if (lock.current || !data?.eligible) return;
    lock.current = true;
    setRes({ state: "busy", msg: "Menyimpan finalisasi…" });
    const on = data.order_number;
    const url = data.source === "shopee" ? `${API}/shopee/orders/${encodeURIComponent(on)}/shella-finalize` : `${API}/orders/${encodeURIComponent(on)}/finalize`;
    try {
      const r = await axios.post(url, {}, opt);
      const already = !!r.data?.already_finalized;
      setRes({ state: "ok", msg: already ? "Sudah Diterima sebelumnya — tidak dicatat ulang" : "Diterima & Difinalisasi — tersimpan di server" });
      playTone(already ? "done" : "success");
      try { window.navigator?.vibrate?.(80); } catch { /* noop */ }
      window.dispatchEvent(new Event("shella-fast-done"));
      if (data.source === "offline") Promise.resolve(onOfflineChanged?.()).catch(() => {});
      await lookup(on, { silent: true });
      if (fromCam.current) setTimeout(() => setCam(true), 900);
    } catch (e) {
      // Status sebenarnya: cek ulang ke server sebelum menyatakan gagal.
      const fresh = await lookup(on, { silent: true });
      if (fresh?.finalized) setRes({ state: "ok", msg: "Diterima & Difinalisasi — terkonfirmasi dari server" });
      else { setRes({ state: "fail", msg: errText(e), retry: !e?.response || e.response.status >= 500 }); playScanBeep({ kind: "err", durationMs: 180 }); }
    } finally { lock.current = false; focus(); }
  };

  // Enter di kolom kosong = tombol utama. Diabaikan saat memuat, <600ms setelah data tampil (Enter ganda scanner), atau ada dialog lain.
  const enterConfirm = () => {
    if (phase !== "ready" || !data) { if (phase === "loading") setNote("Data masih dimuat — tunggu sebentar."); return; }
    if (!readyAt.current || Date.now() - readyAt.current < 600) return;
    if (mis === "pick" || cam || document.querySelector('[role="dialog"],[aria-modal="true"]')) return;
    if (lock.current || res?.state === "busy") return;
    if (mis) { setNote("Barang dilaporkan tidak sesuai — Enter dinonaktifkan. Klik tombol hijau bila tetap ingin menerima."); playTone("blocked"); return; }
    if (data.finalized || res?.state === "ok") { setNote("Barang sudah diterima — tidak dicatat ulang. Silakan scan barcode berikutnya."); playTone("done"); return; }
    if (res?.state === "fail") { if (res.retry) confirm(); return; }
    if (!data.eligible) { setNote("Belum bisa diterima — lihat posisi terakhir di bawah. Enter tidak menerima barang ini."); playTone("blocked"); return; }
    setNote(""); confirm();
  };

  const sendMismatch = async (reason, label) => {
    try {
      await axios.post(`${API}/shella/scan/${encodeURIComponent(data.order_number)}/mismatch`, { reason, source: data.source }, opt);
      setMis({ label }); playScanBeep({ freq: 600, durationMs: 140 });
      lookup(data.order_number, { silent: true });
    } catch (e) { setMis({ label: `Gagal mencatat: ${errText(e)}` }); }
    focus();
  };

  return <section className="afs" data-testid="shella-fast-scan">
    <form className="afs-bar" onSubmit={(e) => { e.preventDefault(); fromCam.current = false; if (code.trim()) lookup(code); else enterConfirm(); }}>
      <ScanLine size={20} aria-hidden="true"/>
      <input ref={inputRef} value={code} onChange={e => setCode(e.target.value)} placeholder="Scan Finalisasi: barcode nota / pesanan…"
        autoComplete="off" autoCorrect="off" autoCapitalize="characters" spellCheck={false} enterKeyHint="search"
        onBlur={() => { if (!cam) setTimeout(() => { const a = document.activeElement; if (!a || a === document.body) inputRef.current?.focus(); }, 150); }}
        data-testid="shella-fast-input"/>
      <button type="button" className="afs-cam" onClick={() => setCam(true)} aria-label="Scan Finalisasi pakai kamera" data-testid="shella-fast-camera"><Camera size={18}/></button>
    </form>
    <div className="afs-hint">Scan barcode → periksa barang → tekan <kbd>Enter</kbd> untuk Terima. Scan tidak langsung memfinalisasi.<br/><small data-testid="shella-fast-tone-legend">{TONE_LEGEND}</small></div>
    {note && <div className="afs-msg afs-warn" role="status" data-testid="shella-fast-note">{note}</div>}
    {recent.length > 1 && <div className="afs-recent" data-testid="shella-fast-recent">
      {recent.map(r => <button key={r.code} type="button" className={r.done ? "done" : ""} onClick={() => { fromCam.current = false; last.current = { code: "", at: 0 }; lookup(r.code); }}
        data-testid={`shella-fast-recent-${r.code}`}>{r.code}{r.done ? " ✓" : ""}</button>)}
    </div>}

    {phase === "loading" && <div className="afs-msg" data-testid="shella-fast-loading"><RefreshCw size={14} className="spin"/> Membaca data…</div>}
    {phase === "error" && <div className="afs-msg afs-err" role="alert" data-testid="shella-fast-error"><XCircle size={16}/> {err}</div>}
    {phase === "channel" && mm && <ChannelMismatchNotice m={mm} testid="shella-fast-channel" onOpen={() => openChannelFor(["shella"], mm.channel, "shella", mm.code)}/>}

    {phase === "ready" && data && <div className={`afs-panel${data.finalized ? " done" : ""}`} data-testid="shella-fast-panel">
      <div className="afs-head">
        <div>
          <span className={`afs-src afs-src-${data.source}`}>{data.source === "shopee" ? "SHOPEE" : "OFFLINE"}</span>
          <strong className="afs-num" data-testid="shella-fast-order">{data.order_number}</strong>
          <span className="afs-cust">{data.customer}</span>
        </div>
        <span className={`afs-state ${data.finalized ? "ok" : data.eligible ? "wait" : "block"}`} data-testid="shella-fast-state">
          {data.finalized ? "Sudah Diterima" : data.eligible ? "Siap Diperiksa" : "Belum Bisa Diterima"}</span>
      </div>
      <div className="afs-steps">
        <span className={scanRec}>{scanRec === "fail" ? "Scan Tidak Tercatat" : "Scan Tercatat"}</span>
        <span className={data.finalized ? "ok" : ""}>Diterima & Difinalisasi</span>
      </div>
      <PositionBanner data={data}/>
      {data.finalized && <div className="afs-msg afs-ok" data-testid="shella-fast-finalized">Sudah Diterima oleh {data.finalized_by || "-"} · {fmt(data.finalized_at)}</div>}
      <ShellaBody data={data}/>
      {data.reasons?.length > 0 && !data.finalized && <div className="afs-msg afs-err" data-testid="shella-fast-reasons">Belum bisa diterima: {data.reasons.join("; ")}</div>}
      {data.mismatch_reports?.length > 0 && <div className="afs-msg afs-warn"><AlertTriangle size={14}/> Pernah dilaporkan tidak sesuai: {data.mismatch_reports.map(m => `${m.reason_label} (${m.actor_name}, ${fmt(m.at)})`).join(" · ")}</div>}
      {res && <div className={`afs-msg afs-${res.state}`} role="status" data-testid="shella-fast-result">
        {res.state === "ok" ? <CheckCircle2 size={16}/> : res.state === "busy" ? <RefreshCw size={14} className="spin"/> : <XCircle size={16}/>} {res.msg}
        {res.state === "fail" && res.retry && <button type="button" className="afs-retry" onClick={confirm} data-testid="shella-fast-retry">Coba Lagi</button>}
      </div>}
      {mis && mis !== "pick" && <div className="afs-msg afs-warn" data-testid="shella-fast-mismatch-done">Tercatat: Barang Tidak Sesuai — {mis.label}. Tidak difinalisasi.</div>}
      {!data.finalized && <div className="afs-actions">
        <button type="button" className="afs-acc" disabled={!data.eligible || res?.state === "busy"} onClick={confirm} data-testid="shella-fast-confirm-btn">
          {res?.state === "busy" ? "Menyimpan…" : <>TERIMA & SELESAIKAN — BARANG LENGKAP{data.eligible && <kbd className="afs-kbd" data-testid="shella-fast-enter-hint">Enter</kbd>}</>}</button>
        <button type="button" className="afs-bad" onClick={() => setMis(mis === "pick" ? null : "pick")} disabled={res?.state === "busy"} data-testid="shella-fast-mismatch-btn">Barang Tidak Sesuai</button>
      </div>}
      <button type="button" className="afs-next" onClick={() => { setData(null); setPhase("idle"); setRes(null); setMis(null); last.current = { code: "", at: 0 }; focus(); }}
        data-testid="shella-fast-next-btn">Scan Berikutnya</button>
      {mis === "pick" && <div className="afs-mis">
        {MISMATCH.map(([k, l]) => <button key={k} type="button" onClick={() => sendMismatch(k, l)} data-testid={`shella-fast-mismatch-${k}`}>{l}</button>)}
      </div>}
    </div>}

    <ScanMockupModal open={cam} onClose={() => { setCam(false); focus(); }} searchOnly title="Scan Finalisasi"
      onOpenOrder={(c) => { fromCam.current = true; setCam(false); lookup(c); }}/>
  </section>;
}

function Sizes({ list }) {
  return <div className="afs-sizes">{list.map((s, k) => <span key={k} className="afs-size"><b>{s.size}</b> {s.color !== "—" ? s.color : ""}{s.variant ? ` · ${s.variant}` : ""} <strong>{s.qty} pcs</strong></span>)}</div>;
}

function ShellaBody({ data }) {
  if (data.source === "offline") {
    const total = data.items.reduce((n, i) => n + (i.qty || 0), 0);
    return <div className="afs-body">
      <img className="afs-thumb" src={`${BASE}${data.thumb}`} alt="Mockup" onError={e => { e.currentTarget.style.visibility = "hidden"; }}/>
      <div className="afs-items">
        <div className="afs-total" data-testid="shella-fast-total">Total harus ada: <b>{total} pcs</b></div>
        {data.items.map(i => <div key={i.key} className="afs-item" data-testid={`shella-fast-item-${i.key}`}>
          <div className="afs-item-name">{i.name}{i.material ? ` · ${i.material}` : ""} {i.done ? <em className="afs-ok">Tuntas</em> : <span className="afs-reason">di {i.stage || "-"}</span>}</div>
          <Sizes list={i.sizes.length ? i.sizes : [{ size: "—", color: i.color || "—", qty: i.qty }]}/>
          {i.from && <div className="afs-note">Dari: {i.from}</div>}
          {i.last && <LastSeen last={i.last} testid={`shella-fast-last-${i.key}`}/>}
        </div>)}
      </div>
    </div>;
  }
  const total = data.products.reduce((n, p) => n + (p.qty || 0), 0);
  return <div className="afs-body">
    <div className="afs-thumbs">{data.jobs.map(j => j.thumb
      ? <img key={j.key} className="afs-thumb" src={`${BASE}${j.thumb}`} alt={j.title || "Mockup"}/>
      : <div key={j.key} className="afs-thumb afs-thumb-empty">Mockup tidak tersedia</div>)}</div>
    <div className="afs-items">
      {data.products_available
        ? <><div className="afs-total" data-testid="shella-fast-total">Total harus ada: <b>{total} pcs</b></div>
          {data.products.map((p, k) => <div key={k} className="afs-item"><div className="afs-item-name">{p.name}</div><Sizes list={[p]}/></div>)}</>
        : <div className="afs-msg afs-warn">Rincian produk Shopee belum tersinkron — periksa sesuai mockup.</div>}
      {data.jobs.map(j => <div key={j.key} className="afs-job" data-testid={`shella-fast-job-${j.key}`}>
        {j.process}{j.title ? ` · ${j.title}` : ""}{j.from ? ` — Dari: ${j.from}` : ""} — {j.finalized_at
          ? <em className="afs-ok">Sudah Diterima ({j.finalized_by})</em> : j.ready ? "siap" : <span className="afs-reason">{j.reason}</span>}
        {!j.finalized_at && !j.ready && j.last && <LastSeen last={j.last} testid={`shella-fast-last-${j.key}`}/>}
      </div>)}
    </div>
  </div>;
}

// Posisi tercatat (bukan bukti fisik): A di divisi lain · B tuntas tapi belum muncul di antrean Shella.
function PositionBanner({ data }) {
  if (data.position === "elsewhere") {
    const firstLast = (data.source === "offline" ? data.items : data.jobs).map(x => x.last).find(Boolean);
    const who = firstLast ? (firstLast.pic || firstLast.last_by) : "";
    return <div className="afs-msg afs-warn afs-pos" data-testid="shella-fast-position">
      <b>Belum masuk antrean Shella</b>{firstLast ? ` — terakhir tercatat di ${firstLast.stage || "-"}${who ? `, ditangani oleh ${who}` : ""}.` : "."}
      {" "}Tanyakan barang ke operator tersebut. Rincian per produk di bawah.</div>;
  }
  if (data.position === "sent_not_visible") return <div className="afs-msg afs-err afs-pos" data-testid="shella-fast-position">
    <b>Perlu diperiksa:</b> semua produk sudah tercatat selesai produksi (dikirim ke Shella), tetapi nota belum muncul di antrean penerimaan (status nota: {data.order_status || "-"}). Laporkan ke Admin/Owner.</div>;
  return null;
}

function LastSeen({ last, testid }) {
  const who = last.pic || last.last_by;
  return <div className="afs-note afs-last" data-testid={testid}>
    Terakhir tercatat: <b>{last.stage || "-"}</b>{who ? <> · ditangani oleh <b>{who}</b></> : " · operator belum tercatat"}
    {last.last_at ? ` · ${fmt(last.last_at)}` : ""}
    {last.tracks?.length > 0 && <div>{last.tracks.join(" · ")}</div>}
    {last.note && <div>{last.note}</div>}
  </div>;
}
